import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

/**
 * Unified Bootstrap Tooltip/Popover Controller
 *
 * Handles both Bootstrap tooltips and popovers through configuration.
 * Uses Stimulus values for flexible setup.
 *
 * Usage (Tooltip - default):
 *   data-controller="tooltip"
 *   data-bs-toggle="tooltip"
 *   title="Tooltip text"
 *
 * Usage (Popover):
 *   data-controller="tooltip"
 *   data-tooltip-type-value="popover"
 *   data-bs-toggle="popover"
 *   data-bs-content="<strong>HTML</strong> content"
 *
 * Available values:
 *   type: "tooltip" (default) or "popover"
 *   trigger: "hover focus" (default for popover), "hover" (default for tooltip)
 *   placement: "top" (default), "bottom", "left", "right"
 *   html: true/false (default: true for popover, false for tooltip)
 *   selector: delegate to descendants matching this selector (one controller
 *     on a container serves e.g. hundreds of chips — see /leaders) instead of
 *     attaching to this element itself. Delegation is hand-rolled here rather
 *     than via Bootstrap's own `selector` option, whose delegated mouseleave
 *     is unreliable and leaves tooltips stuck open.
 */
export default class extends Controller {
  static values = {
    type: { type: String, default: "tooltip" },
    trigger: String,
    placement: { type: String, default: "top" },
    html: Boolean,
    selector: String
  }

  connect() {
    // Presence alone isn't enough: an empty selector would build the invalid
    // selector ", [data-tooltip-delegated]" and throw on every pointer event.
    if (this.hasSelectorValue && this.selectorValue.trim() !== "") {
      this.connectDelegated()
      return
    }

    const ComponentClass = this.isPopover ? bootstrap.Popover : bootstrap.Tooltip
    this.instance = new ComponentClass(this.element, this.options)
  }

  disconnect() {
    if (this.instance) {
      this.instance.dispose()
      this.instance = null
    }

    if (this.delegated) {
      this.element.removeEventListener("mouseover", this.onEnter)
      this.element.removeEventListener("mouseout", this.onLeave)
      this.element.removeEventListener("focusin", this.onEnter)
      this.element.removeEventListener("focusout", this.onLeave)
      document.removeEventListener("show.bs.modal", this.onModalShow)
      document.removeEventListener("turbo:before-cache", this.onBeforeCache)
      this.disposeDelegated()
      this.delegated = null
    }
  }

  // Per-target tooltips created lazily in manual mode; show on the first
  // mouseover/focusin whose target matches the selector, hide when the
  // pointer/focus actually leaves that target.
  connectDelegated() {
    this.delegated = new Map()
    // Bootstrap strips the title attribute when it shows a tooltip, so a
    // selector like "[title]" stops matching after the first show and the
    // element could never be found again to hide it. Elements that already
    // have a tooltip are marked and matched by that mark instead.
    const match = (event) => event.target.closest(`${this.selectorValue}, [data-tooltip-delegated]`)
    this.onEnter = (event) => {
      const target = match(event)
      if (target) this.tipFor(target).show()
    }
    this.onLeave = (event) => {
      const target = match(event)
      if (!target || target.contains(event.relatedTarget)) return
      this.delegated.get(target)?.hide()
    }
    // A click can open a modal over the target, so mouseout never fires and
    // the tooltip would sit stuck behind the backdrop. Hide on the modal
    // actually opening — NOT on every click, which would strand a hidden
    // (trigger: manual) tooltip on still-hovered non-modal elements like the
    // legend chips, where nothing re-shows it until the pointer re-enters.
    this.onModalShow = () => this.delegated.forEach((tip) => tip.hide())
    this.element.addEventListener("mouseover", this.onEnter)
    this.element.addEventListener("mouseout", this.onLeave)
    document.addEventListener("show.bs.modal", this.onModalShow)
    this.element.addEventListener("focusin", this.onEnter)
    this.element.addEventListener("focusout", this.onLeave)
    // disconnect() runs after Turbo snapshots the page, so a tooltip open at
    // navigation time would be cached into the snapshot as an orphan div.
    // Dispose (not hide — hide animates and can outlive the snapshot) first.
    this.onBeforeCache = () => this.disposeDelegated()
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
  }

  disposeDelegated() {
    this.delegated.forEach((tip, target) => {
      tip.dispose()
      target.removeAttribute("data-tooltip-delegated")
    })
    this.delegated.clear()
  }

  tipFor(target) {
    let tip = this.delegated.get(target)
    if (!tip) {
      tip = new bootstrap.Tooltip(target, { ...this.options, trigger: "manual" })
      this.delegated.set(target, tip)
      target.setAttribute("data-tooltip-delegated", "")
    }
    return tip
  }

  get isPopover() {
    return this.typeValue === "popover"
  }

  get options() {
    const defaults = this.isPopover
      ? { trigger: "hover focus", html: true }
      : { trigger: "hover", html: false }

    return {
      trigger: this.hasTriggerValue ? this.triggerValue : defaults.trigger,
      placement: this.placementValue,
      html: this.hasHtmlValue ? this.htmlValue : defaults.html
    }
  }
}
